import React, { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Paper,
  Table,
  Badge,
  ActionIcon,
  Modal,
  Loader,
  Alert,
  Radio,
  UnstyledButton,
} from "@mantine/core";
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { StandardTableShell } from "../common/StandardTable";
import { useProducts } from "../../context/ProductsContext";
import TaxRateForm from "./TaxRateForm";
import {
  TaxRate,
  CreateTaxRateRequest,
  EditTaxRateRequest,
} from "../../types/taxRates";

type DeleteOption = "end_date" | "force_delete" | null;

export default function TaxesSettings() {
  const { businessInfo, services } = useSalon() as {
    businessInfo: { id: string } | null;
    services: any[] | null;
  };
  const { products } = useProducts();
  const { getTaxRates, createTaxRate, updateTaxRate, deleteTaxRate } = useApp();

  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<TaxRate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deletingRate, setDeletingRate] = useState<TaxRate | null>(null);
  const [deleteOption, setDeleteOption] = useState<DeleteOption>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTaxRates = useCallback(async () => {
    if (!businessInfo?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getTaxRates(businessInfo.id);
      setTaxRates(Array.isArray(response) ? response : []);
    } catch (err: any) {
      console.error("Failed to load tax rates:", err);
      setError(err?.message || "Failed to load tax rates");
    } finally {
      setIsLoading(false);
    }
  }, [businessInfo?.id, getTaxRates]);

  useEffect(() => {
    fetchTaxRates();
  }, [fetchTaxRates]);

  const handleOpenAdd = () => {
    setEditingRate(null);
    setSaveError(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (rate: TaxRate) => {
    setEditingRate(rate);
    setSaveError(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingRate(null);
    setSaveError(null);
  };

  const handleSave = async (
    data: CreateTaxRateRequest | EditTaxRateRequest,
  ) => {
    if (!businessInfo?.id) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      if (editingRate) {
        await updateTaxRate(editingRate.uuid, data);
      } else {
        await createTaxRate(businessInfo.id, data);
      }
      await fetchTaxRates();
      handleCloseForm();
    } catch (err: any) {
      console.error("Failed to save tax rate:", err);
      setSaveError(err?.message || "Failed to save tax rate");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDelete = (rate: TaxRate) => {
    setDeletingRate(rate);
    setDeleteOption(null);
  };

  const handleCloseDelete = () => {
    setDeletingRate(null);
    setDeleteOption(null);
  };

  const handleDelete = async () => {
    if (!deletingRate || !deleteOption) return;
    setIsDeleting(true);
    try {
      await deleteTaxRate(deletingRate.uuid, {
        force: deleteOption === "force_delete",
      });
      await fetchTaxRates();
      handleCloseDelete();
    } catch (err: any) {
      console.error("Failed to delete tax rate:", err);
      setError(err?.message || "Failed to delete tax rate");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatRate = (rate: number) => `${(rate * 100).toFixed(3)}%`;

  const isEndDated = (rate: TaxRate): boolean => {
    if (!rate.effective_end_date) return false;
    return new Date(rate.effective_end_date) <= new Date();
  };

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={0}>
          <Title order={3}>Taxes</Title>
          <Text size="sm" c="dimmed">
            Define sales tax rates that apply to products and services in this
            salon.
          </Text>
        </Stack>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleOpenAdd}
          color="var(--color-primary)"
        >
          Add Tax Rate
        </Button>
      </Group>

      {error && (
        <Alert color="red" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}

      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Changes to a tax rate automatically recompute the tax amount on every
        unpaid / partially paid transaction that includes an affected product or
        service. Historical paid transactions are frozen at the rate they were
        charged.
      </Alert>

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader size="md" />
        </Group>
      ) : (
        <StandardTableShell ariaLabel="Tax rates">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Rate</Table.Th>
              <Table.Th>Jurisdiction</Table.Th>
              <Table.Th>Effective</Table.Th>
              <Table.Th style={{ textAlign: "right" }}>Products</Table.Th>
              <Table.Th style={{ textAlign: "right" }}>Services</Table.Th>
              <Table.Th style={{ textAlign: "center" }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {taxRates.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center" c="dimmed" py="md">
                    No tax rates configured. Click &quot;Add Tax Rate&quot; to
                    create one.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              taxRates.map((rate) => (
                <Table.Tr key={rate.uuid}>
                  <Table.Td>
                    <Group gap="xs">
                      <Text fw={500}>{rate.name}</Text>
                      {isEndDated(rate) && (
                        <Badge color="gray" size="xs">
                          Inactive
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{formatRate(rate.rate)}</Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">{rate.jurisdiction_level}</Text>
                      {rate.jurisdiction_name && (
                        <Text size="xs" c="dimmed">
                          {rate.jurisdiction_name}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">{rate.effective_start_date}</Text>
                      <Text size="xs" c="dimmed">
                        {rate.effective_end_date || "No end date"}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    <Text>{rate.product_ids.length}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    <Text>{rate.salon_service_ids.length}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "center" }}>
                    <Group gap="xs" justify="center">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={() => handleOpenEdit(rate)}
                        aria-label="Edit"
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleOpenDelete(rate)}
                        aria-label="Delete"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </StandardTableShell>
      )}

      {/* Add / Edit Modal */}
      <Modal
        opened={isFormOpen}
        onClose={handleCloseForm}
        title={editingRate ? "Edit Tax Rate" : "Add Tax Rate"}
        size="lg"
        closeOnClickOutside={false}
      >
        <TaxRateForm
          initialRate={editingRate}
          availableProducts={products || []}
          availableServices={services || []}
          onSubmit={handleSave}
          onCancel={handleCloseForm}
          isSaving={isSaving}
          saveError={saveError}
        />
      </Modal>

      {/* Delete Modal — clickable card radio options */}
      <Modal
        opened={deletingRate !== null}
        onClose={handleCloseDelete}
        title="Delete Tax Rate"
        size="md"
      >
        {deletingRate && (
          <Stack gap="md">
            <Text>
              How would you like to remove <strong>{deletingRate.name}</strong>?
            </Text>

            <Radio.Group
              value={deleteOption || ""}
              onChange={(val) => setDeleteOption(val as DeleteOption)}
            >
              <Stack gap="sm">
                <UnstyledButton
                  onClick={() => setDeleteOption("end_date")}
                  style={{ width: "100%" }}
                >
                  <Paper
                    withBorder
                    p="sm"
                    style={{
                      cursor: "pointer",
                      borderColor:
                        deleteOption === "end_date"
                          ? "var(--color-primary)"
                          : undefined,
                      backgroundColor:
                        deleteOption === "end_date"
                          ? "var(--mantine-color-teal-0, #f0fafb)"
                          : undefined,
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          Set the end date to today
                        </Text>
                        <Text size="xs" c="dimmed">
                          Stops applying this tax rate to new bookings, but
                          keeps the historical audit trail intact. Any unpaid
                          transactions that were using this rate will be
                          recomputed.
                        </Text>
                      </Stack>
                      <Radio value="end_date" tabIndex={-1} />
                    </Group>
                  </Paper>
                </UnstyledButton>

                <UnstyledButton
                  onClick={() => setDeleteOption("force_delete")}
                  style={{ width: "100%" }}
                >
                  <Paper
                    withBorder
                    p="sm"
                    style={{
                      cursor: "pointer",
                      borderColor:
                        deleteOption === "force_delete"
                          ? "var(--mantine-color-red-6, #e03131)"
                          : undefined,
                      backgroundColor:
                        deleteOption === "force_delete"
                          ? "var(--mantine-color-red-0, #fff5f5)"
                          : undefined,
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={2}>
                        <Text size="sm" fw={500} c="red">
                          Delete tax rate forever
                        </Text>
                        <Text size="xs" c="dimmed">
                          This is irreversible and may lead to inconsistencies
                          in reporting if this tax was used in the past.
                        </Text>
                      </Stack>
                      <Radio value="force_delete" color="red" tabIndex={-1} />
                    </Group>
                  </Paper>
                </UnstyledButton>
              </Stack>
            </Radio.Group>

            <Group justify="space-between" mt="sm">
              <Button
                variant="default"
                onClick={handleCloseDelete}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDelete}
                loading={isDeleting}
                disabled={!deleteOption}
              >
                Submit
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
